<template>
  <div class="slot-strip" :style="{ '--sp-per-page': daysPerPage }">
    <div class="slot-strip-head">
      <button
        type="button"
        class="slot-strip-nav"
        :disabled="atStart"
        :aria-label="'Earlier times' | translate"
        @click="page(-1)"
      >
        <i class="fas fa-chevron-left"></i>
      </button>

      <div class="slot-strip-range">{{ visibleRange }}</div>

      <button
        type="button"
        class="slot-strip-nav"
        :disabled="atEnd"
        :aria-label="'Later times' | translate"
        @click="page(1)"
      >
        <i class="fas fa-chevron-right"></i>
      </button>
    </div>

    <div
      class="slot-strip-days"
      role="group"
      :aria-label="'Available times' | translate"
      @touchstart.passive="onTouchStart"
      @touchend.passive="onTouchEnd"
    >
      <div v-for="day in visibleColumns" :key="day.key" class="slot-day">
        <div class="slot-day-head" :class="{ 'is-today': day.isToday, 'is-selected': day.key === selectedDayKey }">
          <span class="slot-day-weekday">{{ day.weekdayLabel | translate }}</span>
          <span class="slot-day-date">{{ day.shortDate }}</span>
        </div>

        <div class="slot-day-times">
          <template v-for="cell in day.cells">
            <button
              v-if="cell.slot"
              :key="cell.key"
              type="button"
              class="slot-time"
              :class="{ 'is-selected': isSelected(cell.slot) }"
              :style="{ gridRow: cell.row }"
              :aria-pressed="String(isSelected(cell.slot))"
              :title="`${day.title}, ${cell.slot.label}`"
              @click="choose(cell.slot)"
            >
              {{ cell.slot.startTime }}
            </button>
            <span v-else :key="`${cell.key}-else`" class="slot-empty" :style="{ gridRow: cell.row }" aria-hidden="true">&ndash;</span>
          </template>
        </div>
      </div>
    </div>

    <div v-if="hasMoreSlots" class="slot-strip-more">
      <button type="button" class="slot-strip-more-btn" @click="showMoreSlots">
        {{ 'Show more' | translate }}
        <i class="fas fa-chevron-down"></i>
      </button>
    </div>

    <div v-if="showZone" class="slot-strip-zone">
      <i class="fas fa-globe"></i>
      {{ zoneLabel }}
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

/**
 * znanylekarz style slot picker: a grid of day columns, each holding that day's
 * full timetable, `daysPerPage` columns wide. The arrows - or a horizontal
 * swipe - swap in the next page of days and stop at the edges of the offered
 * range. Nothing scrolls: every time is listed, so the strip is as tall as the
 * page's busiest day. Times share rows across the columns - a day that cannot
 * offer 10:00 shows a disabled `-` in that row, so 11:00 still lines up with
 * 11:00 in its neighbours.
 *
 * Slots come from `BookingSlotGenerator` as
 * `{ start_at, end_at, start_min, end_min }` - `start_at` / `end_at` are real
 * UTC instants, while `start_min` / `end_min` are minutes into a local day of
 * the *calendar* timezone. Only the instants are used here: minutes alone cannot
 * say which local date a slot belongs to (an early morning slot in a
 * positive-offset zone falls on the previous UTC day), so every slot is
 * converted with `timezone` and grouped by that zone's date.
 *
 * Only the days between the first and the last slot get a column - the range is
 * walked day by day, so a day that is fully booked shows as an empty column
 * rather than closing the gap and making two distant dates look adjacent.
 *
 * It is a `v-model` component: `value` is the selected slot object and `input`
 * is emitted with the original object, so whatever submits the booking keeps
 * the server's shape.
 */
export default {
  name: 'SlotPicker',

  props: {
    /** Raw slots, as rendered by `BookingSlotGenerator::forRange()`. */
    slots: { type: Array, default: () => [] },
    /**
     * IANA timezone the slots are displayed in (the course calendar's zone).
     * `null` falls back to the visitor's browser timezone.
     */
    timezone: { type: String, default: null },
    /** Currently selected slot object. */
    value: { type: Object, default: null },
    /** Locale used for the day / date labels. Defaults to the page language. */
    locale: { type: String, default: null },
    /** Day columns per page - also the page size of the arrows. */
    daysPerPage: { type: Number, default: 4 },
  },

  data() {
    return {
      /** Index of the page of day columns on screen; the arrows step through it. */
      pageIndex: 0,
      /**
       * Whether the full timetable is shown. Collapsed by default: a column
       * lists at most `collapsedRows` times and a "show more" button reveals
       * the rest, so a busy day does not stretch the strip down the page.
       */
      expanded: false,
    }
  },

  computed: {
    resolvedLocale() {
      if (this.locale) return this.locale

      // `vuex-i18n` keeps the active locale in its own store module; `window.lang`
      // is only set by the older bundles, so it is a fallback.
      const state = this.$store ? this.$store.state : null
      const locale = (state && state.i18n && state.i18n.locale) || (typeof window !== 'undefined' && window.lang)

      return locale || 'en'
    },

    /** Slots with their display values, oldest first. */
    normalizedSlots() {
      return this.slots
        .map((raw, index) => {
          const start = this.zoned(raw.start_at)
          if (!start) return null

          const end = this.zoned(raw.end_at)
          const label = end ? `${start.format('HH:mm')} – ${end.format('HH:mm')}` : start.format('HH:mm')

          return {
            raw,
            key: raw.start_at || String(index),
            day: start.format('YYYY-MM-DD'),
            start,
            startTime: start.format('HH:mm'),
            /** Minutes into the displayed day - the row a time belongs to. */
            minutes: start.hour() * 60 + start.minute(),
            label,
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.start.valueOf() - b.start.valueOf())
    },

    /** `YYYY-MM-DD` of the day it is *in the display timezone*. */
    todayKey() {
      return (this.timezone ? dayjs().tz(this.timezone) : dayjs()).format('YYYY-MM-DD')
    },

    /**
     * Every day in the offered range, oldest first - days with no slots included.
     * The range runs from the first slot's day to the last one's and is walked a
     * day at a time, so a fully booked day keeps its column instead of letting
     * two distant dates sit next to each other as if consecutive.
     */
    days() {
      const byDay = this.normalizedSlots.reduce((groups, slot) => {
        groups[slot.day] = groups[slot.day] || []
        groups[slot.day].push(slot)
        return groups
      }, {})

      const keys = Object.keys(byDay).sort()
      if (!keys.length) return []

      const tomorrow = (this.timezone ? dayjs().tz(this.timezone) : dayjs()).add(1, 'day').format('YYYY-MM-DD')
      const lastKey = keys[keys.length - 1]
      const days = []

      for (let date = dayjs(keys[0]); date.format('YYYY-MM-DD') <= lastKey; date = date.add(1, 'day')) {
        const key = date.format('YYYY-MM-DD')
        const isToday = key === this.todayKey

        days.push({
          key,
          isToday,
          slots: byDay[key] || [],
          weekdayLabel: isToday
            ? this.$t('Today')
            : key === tomorrow
            ? this.$t('Tomorrow')
            : this.formatIntl(date.toDate(), { weekday: 'short' }),
          shortDate: this.formatIntl(date.toDate(), { day: 'numeric', month: 'short' }),
          title: this.formatIntl(date.toDate(), { weekday: 'long', day: 'numeric', month: 'long' }),
        })
      }

      return days
    },

    selectedDayKey() {
      const selected = this.value ? this.zoned(this.value.start_at) : null
      return selected ? selected.format('YYYY-MM-DD') : null
    },

    /** The day columns on screen: one page of days, starting at `pageIndex`. */
    visibleDays() {
      const start = this.pageIndex * this.daysPerPage
      return this.days.slice(start, start + this.daysPerPage)
    },

    pageCount() {
      return Math.max(1, Math.ceil(this.days.length / this.daysPerPage))
    },

    /**
     * The start times offered anywhere on this page, oldest first.
     *
     * Both the real chips and the disabled pauses are laid out against this one
     * axis, so a day that cannot offer 10:00 shows a `-` in the 10:00 row
     * instead of pulling 11:00 up out of line with its neighbours. The times are
     * taken from the slots that exist - a page never invents an hour that
     * nothing offers - and compared by minute of the day, so 09:30 and 10:00
     * stay distinct.
     */
    /**
     * How many times a column lists while collapsed. The page axis is capped
     * at this many rows, so a day with a long timetable does not stretch the
     * strip down the page - the rest sits behind the "show more" button.
     */
    collapsedRows() {
      return 8
    },

    /**
     * Every start time offered anywhere on this page, oldest first.
     *
     * The times are taken from the slots that exist - a page never invents an
     * hour that nothing offers - and compared by minute of the day, so 09:30
     * and 10:00 stay distinct.
     */
    allPageTimes() {
      const times = []

      this.visibleDays.forEach((day) =>
        day.slots.forEach((slot) => {
          if (!times.some((time) => time.minutes === slot.minutes)) times.push(slot)
        }),
      )

      return times.sort((a, b) => a.minutes - b.minutes)
    },

    /**
     * The rows the columns are laid out against: `allPageTimes`, capped at
     * `collapsedRows` while collapsed.
     *
     * Both the real chips and the disabled pauses are laid out against this one
     * axis, so a day that cannot offer 10:00 shows a `-` in the 10:00 row
     * instead of pulling 11:00 up out of line with its neighbours.
     */
    pageTimes() {
      const times = this.allPageTimes
      return this.expanded ? times : times.slice(0, this.collapsedRows)
    },

    /**
     * Whether the page offers more times than the collapsed view lists - the
     * "show more" button is shown only then.
     */
    hasMoreSlots() {
      return !this.expanded && this.allPageTimes.length > this.collapsedRows
    },

    /**
     * The day columns on screen, each carrying one cell per row of `pageTimes`:
     * the slot at that time, or `null` where the day has none. Days without a
     * single slot still get a column, so the day axis never hides a gap.
     */
    visibleColumns() {
      const times = this.pageTimes

      return this.visibleDays.map((day) => ({
        ...day,
        cells: times.map((time, index) => ({
          key: `${day.key}-${time.startTime}`,
          row: index + 1,
          slot: day.slots.find((slot) => slot.minutes === time.minutes) || null,
        })),
      }))
    },

    atStart() {
      return this.pageIndex <= 0
    },

    atEnd() {
      return this.pageIndex >= this.pageCount - 1
    },

    /** The dates currently on screen, e.g. `16–19 Oct`. */
    visibleRange() {
      if (!this.visibleDays.length) return ''

      return this.rangeLabel(this.visibleDays[0], this.visibleDays[this.visibleDays.length - 1])
    },

    /** Stating the timezone only pays off when it is not the visitor's own. */
    showZone() {
      const zone = this.timezone
      return !!zone && zone !== this.browserTimezone()
    },

    /** `Europe/Warsaw (GMT+2)` - makes it obvious whose clock the times are on. */
    zoneLabel() {
      const zone = this.timezone
      if (!zone) return null

      const short = this.zoneShortName(zone)
      return short ? `${zone} (${short})` : zone
    },
  },

  watch: {
    slots() {
      this.$nextTick(this.afterDaysChange)
    },
    value() {
      this.$nextTick(this.afterDaysChange)
    },
  },

  mounted() {
    this.afterDaysChange()
  },

  methods: {
    /** The instant in the display timezone, or `null` when it cannot be parsed. */
    zoned(value) {
      if (!value) return null
      const parsed = dayjs(value)
      if (!parsed.isValid()) return null
      return this.timezone ? parsed.tz(this.timezone) : parsed
    },

    choose(slot) {
      this.$emit('input', slot.raw)
      this.$emit('slot-selected', slot.raw)
    },

    isSelected(slot) {
      return !!this.value && this.value.start_at === slot.raw.start_at
    },

    /** Swap in the next / previous page of day columns. */
    page(direction) {
      const next = this.pageIndex + direction
      if (next < 0 || next > this.pageCount - 1) return
      this.pageIndex = next
    },

    /** Reveal the times hidden behind the "show more" button. */
    showMoreSlots() {
      this.expanded = true
    },

    onTouchStart(event) {
      const touch = event.changedTouches && event.changedTouches[0]
      this._touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null
    },

    /**
     * A clearly horizontal flick pages; anything else is left to the browser, so
     * scrolling the checkout column still works.
     */
    onTouchEnd(event) {
      const from = this._touchStart
      this._touchStart = null

      const touch = event.changedTouches && event.changedTouches[0]
      if (!from || !touch) return

      const dx = touch.clientX - from.x
      const dy = touch.clientY - from.y
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return

      this.page(dx < 0 ? 1 : -1)
    },

    afterDaysChange() {
      if (this.pageIndex > this.pageCount - 1) this.pageIndex = this.pageCount - 1
      this.revealSelected()
    },

    /**
     * Put the selected slot's day on screen, so a pre-filled booking shows which
     * day it belongs to instead of hiding it on another page.
     */
    revealSelected() {
      const index = this.days.findIndex((day) => day.key === this.selectedDayKey)
      if (index < 0) return

      const start = this.pageIndex * this.daysPerPage
      if (index >= start && index < start + this.daysPerPage) {
        this.revealSelectedRow()
        return
      }

      this.pageIndex = Math.floor(index / this.daysPerPage)
    },

    /**
     * Expand the collapsed view when the selected time sits below the
     * `collapsedRows` cap, so a pre-filled booking never hides its own slot
     * behind the "show more" button.
     */
    revealSelectedRow() {
      if (this.expanded || !this.value) return

      const day = this.visibleDays.find((item) => item.key === this.selectedDayKey)
      if (!day) return

      const selected = this.normalizedSlots.find((slot) => slot.raw.start_at === this.value.start_at)
      if (!selected) return

      const row = this.allPageTimes.findIndex((time) => time.minutes === selected.minutes)
      if (row >= this.collapsedRows) this.expanded = true
    },

    /** `Oct 16 – 19` (`16–19 paź` in Polish): the range in the locale's own order. */
    rangeLabel(first, last) {
      if (!first) return ''
      if (!last || first.key === last.key) return first.title

      const formatter = this.intlFormatter({ day: 'numeric', month: 'short' })
      if (!formatter) return `${first.shortDate} – ${last.shortDate}`

      const from = dayjs(first.key).toDate()
      const to = dayjs(last.key).toDate()

      if (typeof formatter.formatRange === 'function') {
        try {
          return formatter.formatRange(from, to)
        } catch (error) {
          /* a range the browser cannot express - fall back to a plain pair */
        }
      }

      return `${formatter.format(from)} – ${formatter.format(to)}`
    },

    /** `Intl` formatter for the resolved locale, `null` when the locale is not known. */
    intlFormatter(options) {
      try {
        return new Intl.DateTimeFormat(this.resolvedLocale, options)
      } catch (error) {
        return null
      }
    },

    browserTimezone() {
      return typeof Intl !== 'undefined' && Intl.DateTimeFormat
        ? Intl.DateTimeFormat().resolvedOptions().timeZone
        : null
    },

    /** `GMT+2` / `CEST` for the given zone, `null` when the browser does not know it. */
    zoneShortName(zone) {
      try {
        const parts = new Intl.DateTimeFormat(this.resolvedLocale, {
          timeZone: zone,
          timeZoneName: 'short',
        }).formatToParts(new Date())

        const part = parts.find((item) => item.type === 'timeZoneName')
        return part ? part.value : null
      } catch (error) {
        return null
      }
    },

    formatIntl(input, options) {
      const date = input instanceof Date ? input : dayjs.isDayjs(input) ? input.toDate() : new Date(input)
      const formatter = this.intlFormatter(options)
      return formatter ? formatter.format(date) : dayjs(date).format('YYYY-MM-DD')
    },
  },
}
</script>

<style scoped>
/*
 * Sized for the narrow checkout column (~310px on a phone): `daysPerPage`
 * columns share the width, so the strip swaps whole pages of days rather than
 * scrolling sideways. Nothing is clipped or scrolled - a long day simply makes
 * the strip taller - and there are no rules or boxes around the times, only the
 * chips themselves carry a background.
 */
.slot-strip {
  --sp-gap: 0.35rem;
  --sp-chip-gap: 0.25rem;
  --sp-chip-height: 1.8rem;
  --sp-muted: var(--bs-secondary-color);

  font-size: 0.75rem;
  line-height: 1.2;
}

.slot-strip-head {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0 0.1rem 0.4rem;
}

.slot-strip-range {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 600;
  text-align: center;
  text-transform: capitalize;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.slot-strip-nav {
  flex: 0 0 auto;
  width: 1.9rem;
  height: 1.9rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--sp-muted);
  font-size: 0.75rem;
  line-height: 1;
}

.slot-strip-nav:hover:not(:disabled) {
  background: var(--bs-secondary-bg);
  color: var(--bs-body-color);
}

.slot-strip-nav:disabled {
  opacity: 0.3;
}

/* ------------------------------- days ----------------------------------- */
.slot-strip-days {
  display: grid;
  grid-template-columns: repeat(var(--sp-per-page), minmax(0, 1fr));
  gap: var(--sp-gap);
  align-items: start;
}

.slot-day {
  min-width: 0;
}

.slot-day-head {
  padding-bottom: 0.3rem;
  text-align: center;
  color: var(--sp-muted);
}

.slot-day-head.is-today .slot-day-date {
  color: var(--bs-primary);
}

.slot-day-head.is-selected .slot-day-weekday {
  color: var(--bs-primary);
}

.slot-day-weekday,
.slot-day-date {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.slot-day-weekday {
  font-size: 0.6rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.slot-day-date {
  color: var(--bs-body-color);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: capitalize;
}

.slot-day-times {
  display: grid;
  grid-auto-rows: var(--sp-chip-height);
  gap: var(--sp-chip-gap);
  /* One cell per row of the page axis, so a missing time keeps its row. */
  grid-auto-flow: row;
}

.slot-time {
  min-width: 0;
  padding: 0 0.1rem;
  border: 0;
  border-radius: 0.4rem;
  background: var(--bs-secondary-bg);
  color: var(--bs-body-color);
  font-size: 0.8rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.slot-time:hover,
.slot-time:focus-visible {
  background: var(--bs-tertiary-bg);
  color: var(--bs-primary);
}

.slot-time.is-selected,
.slot-time.is-selected:hover {
  background: var(--bs-primary);
  color: #fff;
  font-weight: 600;
}

/* A time this day cannot offer: the row is held so the columns stay in line. */
.slot-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--bs-tertiary-color);
  user-select: none;
}

/* ------------------------------ show more ------------------------------- */
.slot-strip-more {
  display: flex;
  justify-content: center;
  padding-top: 0.4rem;
}

.slot-strip-more-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.25rem 0.75rem;
  border: 0;
  border-radius: 0.4rem;
  background: transparent;
  color: var(--bs-primary);
  font-size: 0.7rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.slot-strip-more-btn:hover {
  background: var(--bs-secondary-bg);
}

.slot-strip-more-btn i {
  font-size: 0.6rem;
}

.slot-strip-zone {
  padding-top: 0.4rem;
  color: var(--sp-muted);
  font-size: 0.6rem;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
